import { t } from "../../i18n";
import { ExternalLink, FileText, Link2 } from "lucide-react";
import { asRecord, domain, stringValue } from "./ToolResultView.utils";
import { CopyButton } from "../CopyButton";
import { ResultLabel } from "../ResultLabel";
import { Markdown } from "../Markdown";
import { GenericResult } from "./GenericResult";

export function FetchResult({ result }: { result: unknown }) {
  const values = asRecord(result);
  if (!values) return <GenericResult value={result} />;
  const url = stringValue(values.url);
  const content = stringValue(values.content);
  const links = Array.isArray(values.links)
    ? values.links.filter((link): link is string => typeof link === "string")
    : [];
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-lumen-200 bg-lumen-50/60 p-3 dark:border-lumen-900/60 dark:bg-lumen-950/20">
        <div className="flex items-start gap-2">
          <div className="grid size-8 shrink-0 place-items-center rounded-lg bg-white text-lumen-700 shadow-sm dark:bg-white/10 dark:text-lumen-300">
            <FileText size={15} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-stone-800 dark:text-stone-100">
              {stringValue(values.title) || "Web page"}
            </div>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="mt-0.5 flex items-center gap-1 truncate text-xs text-lumen-700 hover:underline dark:text-lumen-300"
              >
                {domain(url)} <ExternalLink size={11} />
              </a>
            )}
          </div>
          {values.truncated === true && (
            <span className="ml-auto shrink-0 rounded-full bg-amber-100 px-2 py-1 text-[10px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
              {t("Truncated")}
            </span>
          )}
        </div>
      </div>
      {content && (
        <div className="rounded-xl border border-stone-200 bg-white/70 p-3 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="flex items-center justify-between gap-2">
            <ResultLabel>{t("Page content")}</ResultLabel>
            <CopyButton value={content} />
          </div>
          <div className="text-sm leading-6 text-stone-600 dark:text-stone-300">
            <Markdown>{content}</Markdown>
          </div>
        </div>
      )}
      {links.length > 0 && (
        <div>
          <ResultLabel>
            {t("Links on this page · {{count}}", { count: links.length })}
          </ResultLabel>
          <div className="flex flex-wrap gap-1.5">
            {links.slice(0, 12).map((link) => (
              <a
                key={link}
                href={link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex max-w-full items-center gap-1 rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-semibold text-stone-600 hover:bg-lumen-50 hover:text-lumen-700 dark:bg-white/10 dark:text-stone-300 dark:hover:bg-lumen-900/30 dark:hover:text-lumen-300"
              >
                <Link2 size={11} />{" "}
                <span className="truncate">{domain(link)}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
